import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ProgereText } from "@/components/ProgereText";
import { ModuleIcon } from "@/components/ModuleIcon";
import { MODULES } from "@/data/constants";
import { useI18n } from "@/context/I18nContext";
import { AlertTriangle, ArrowRight, Check, FileSpreadsheet, Mail, Printer, Smartphone, StickyNote, FolderX } from "lucide-react";

const CHAOS = [
  { icon: FileSpreadsheet, rot: -6, x: -10 },
  { icon: Mail, rot: 5, x: 20 },
  { icon: Smartphone, rot: -3, x: 0 },
  { icon: StickyNote, rot: 7, x: -18 },
  { icon: FolderX, rot: -8, x: 12 },
  { icon: Printer, rot: 4, x: -4 },
];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] } }),
};

export function BeforeAfterSection() {
  const navigate = useNavigate();
  const { t } = useI18n();

  return (
    <section id="pourquoi" className="relative py-24 lg:py-32 overflow-hidden" data-testid="before-after-section">
      <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] pg-radial-gold opacity-40" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <motion.div
          className="max-w-2xl"
          initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.4 }} variants={fadeUp}
        >
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#dc143c]">{t("ba.eyebrow")}</span>
          <h2 className="mt-3 text-4xl sm:text-5xl font-extrabold text-white tracking-tight leading-[1.05]">
            {t("ba.titleA")} <span className="text-[#dc143c]">{t("ba.titleB")}</span>
          </h2>
          <p className="mt-4 text-white/60 text-lg">
            {t("ba.desc")}
          </p>
        </motion.div>

        <div className="mt-14 grid lg:grid-cols-[1fr_auto_1fr] gap-8 lg:gap-6 items-center">
          {/* AVANT — chaos */}
          <motion.div
            className="relative rounded-2xl border border-white/10 bg-[#0a0a0a] p-6 sm:p-8 min-h-[340px]"
            initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={fadeUp}
            data-testid="before-panel"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-mono uppercase tracking-wider text-white/50">
              <AlertTriangle className="w-3.5 h-3.5 text-[#dc143c]" /> {t("ba.before")}
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              {CHAOS.map((c, i) => (
                <motion.div
                  key={i}
                  custom={i}
                  variants={{
                    hidden: { opacity: 0, scale: 0.9, rotate: 0 },
                    show: { opacity: 1, scale: 1, rotate: c.rot, transition: { delay: 0.1 + i * 0.07, duration: 0.4 } },
                  }}
                  style={{ marginLeft: c.x }}
                  className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/60 px-3 py-2 text-sm text-white/55 shadow-lg"
                >
                  <c.icon className="w-4 h-4 text-white/35" />
                  {t("ba.chaos")[i]}
                </motion.div>
              ))}
            </div>
            <p className="mt-6 text-sm text-white/40 leading-relaxed">
              {t("ba.beforeText")}
            </p>
          </motion.div>

          {/* Flèche */}
          <motion.div
            className="flex items-center justify-center"
            initial={{ opacity: 0, scale: 0.6 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="flex h-14 w-14 items-center justify-center rounded-full border border-[#EAB308]/40 bg-[#EAB308]/10">
              <ArrowRight className="w-6 h-6 text-[#EAB308] lg:block hidden" />
              <ArrowRight className="w-6 h-6 text-[#EAB308] rotate-90 lg:hidden" />
            </div>
          </motion.div>

          {/* APRÈS — hub PROGÈRE */}
          <motion.div
            className="relative rounded-2xl border border-[#EAB308]/30 bg-gradient-to-br from-[#12100a] via-[#0a0a0a] to-black p-6 sm:p-8 min-h-[340px]"
            initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={fadeUp}
            data-testid="after-panel"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-[#EAB308]/40 bg-[#EAB308]/10 px-3 py-1 text-[11px] font-mono uppercase tracking-wider text-[#EAB308]">
              <Check className="w-3.5 h-3.5" /> {t("ba.withP")} <ProgereText />
            </div>
            <div className="mt-6 grid grid-cols-3 gap-3">
              {MODULES.map((m, i) => {
                const accent = m.color === "gold" ? "#EAB308" : "#dc143c";
                return (
                  <motion.div
                    key={m.key}
                    custom={i}
                    variants={{
                      hidden: { opacity: 0, y: 12 },
                      show: { opacity: 1, y: 0, transition: { delay: 0.15 + i * 0.07, duration: 0.4 } },
                    }}
                    className="flex flex-col items-center justify-center gap-2 rounded-lg border border-white/10 bg-black/50 p-3 text-center"
                    style={{ boxShadow: `inset 0 0 0 1px ${accent}18` }}
                  >
                    <ModuleIcon name={m.icon} className="w-5 h-5" style={{ color: accent }} />
                    <span className="text-[10px] font-mono uppercase tracking-wide text-white/50">
                      {m.name.replace(/^PROG[ÈE]RE\s*/i, "")}
                    </span>
                  </motion.div>
                );
              })}
            </div>
            <p className="mt-6 text-sm text-white/60 leading-relaxed">
              {t("ba.afterText")}
            </p>
          </motion.div>
        </div>

        <motion.div
          className="mt-12 flex justify-center"
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }}
        >
          <button
            data-testid="before-after-cta"
            onClick={() => navigate("/tarification")}
            className="group inline-flex items-center gap-2 rounded-xl bg-[#EAB308] px-7 py-4 text-base font-bold text-black transition-transform hover:-translate-y-0.5"
          >
            {t("ba.cta")}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
